import { useQuery } from "@tanstack/react-query";
import api, { fmtTL, CATEGORIES, EXPENSE_CATEGORIES } from "@/lib/api";
import { BarChart3, PieChart as PieIcon, Wallet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

export default function Reports() {
  const { data } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard/summary")).data });

  if (!data) return <div className="text-slate-400 text-sm py-20 text-center">Yükleniyor…</div>;

  const branchData = data.branch_breakdown.map((b) => ({
    name: b.name.replace(" Şubesi", "").replace(" Şube", ""),
    Toplam: Math.round(b.total),
  }));

  const catData = [
    ...data.category_breakdown.map((c) => ({
      name: CATEGORIES[c.category]?.label || c.category,
      value: c.amount,
      color: CATEGORIES[c.category]?.color || "#94A3B8",
    })),
    ...(data.expense_breakdown || []).map((c) => ({
      name: EXPENSE_CATEGORIES[c.category]?.label || c.category,
      value: c.amount,
      color: EXPENSE_CATEGORIES[c.category]?.color || "#94A3B8",
    })),
  ];

  const salaryVsBills = [
    { name: "Maaşlar", value: data.salary_total, color: "#3B82F6" },
    { name: "Faturalar", value: data.bills_total, color: "#F59E0B" },
    { name: "Masraflar", value: data.expense_total, color: "#10B981" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Raporlar & Analiz</h1>
        <p className="text-slate-400 text-sm mt-1">Şube, kategori ve maaş dağılım analizleri</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400">Toplam Gider</div>
          <div className="text-2xl font-heading font-bold text-white tabular mt-1">{fmtTL(data.total_expense)}</div>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400">Maaş / Fatura Oranı</div>
          <div className="text-2xl font-heading font-bold text-white tabular mt-1">
            {data.total_expense ? Math.round((data.salary_total / data.total_expense) * 100) : 0}% / {data.total_expense ? Math.round((data.bills_total / data.total_expense) * 100) : 0}%
          </div>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400">Ödenmemiş Fatura</div>
          <div className="text-2xl font-heading font-bold text-rose-400 tabular mt-1">{fmtTL(data.unpaid_total)}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-400" /> Şube Bazlı Toplam Gider</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={branchData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
              <XAxis type="number" stroke="#64748b" fontSize={11} tickFormatter={(v) => `${v / 1000}k`} />
              <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={80} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #334155", borderRadius: 12, color: "#fff" }} formatter={(v) => fmtTL(v)} />
              <Bar dataKey="Toplam" fill="#8B5CF6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2"><PieIcon className="w-5 h-5 text-amber-400" /> Fatura Kategori Dağılımı</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => e.name}>
                {catData.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #334155", borderRadius: 12, color: "#fff" }} formatter={(v) => fmtTL(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-6 lg:col-span-2">
          <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-400" /> Maaş vs Fatura Karşılaştırması</h3>
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <ResponsiveContainer width="100%" height={220} className="max-w-xs">
              <PieChart>
                <Pie data={salaryVsBills} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={4}>
                  {salaryVsBills.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #334155", borderRadius: 12, color: "#fff" }} formatter={(v) => fmtTL(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-3 w-full">
              <div className="flex justify-between items-center rounded-xl bg-slate-800/50 px-4 py-3">
                <span className="text-slate-300 text-sm flex items-center gap-2"><span className="w-3 h-3 rounded bg-blue-500" /> Maaş Giderleri</span>
                <span className="font-heading font-bold text-white tabular">{fmtTL(data.salary_total)}</span>
              </div>
              <div className="flex justify-between items-center rounded-xl bg-slate-800/50 px-4 py-3">
                <span className="text-slate-300 text-sm flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-500" /> Fatura Giderleri</span>
                <span className="font-heading font-bold text-white tabular">{fmtTL(data.bills_total)}</span>
              </div>
              <div className="flex justify-between items-center rounded-xl bg-slate-800/50 px-4 py-3">
                <span className="text-slate-300 text-sm flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-500" /> Masraf Giderleri</span>
                <span className="font-heading font-bold text-white tabular">{fmtTL(data.expense_total)}</span>
              </div>
              <div className="flex justify-between items-center rounded-xl bg-blue-600/15 border border-blue-600/30 px-4 py-3">
                <span className="text-white text-sm font-semibold">Genel Toplam</span>
                <span className="font-heading font-bold text-white tabular">{fmtTL(data.total_expense)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
